<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useBranding } from 'shared/composables/useBranding';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const searchQuery = ref('');
const selectedCategory = ref('all');
const uiFlags = getters['integrations/getUIFlags'];

const nativeIntegrations = computed(() =>
  [
    {
      id: 'navi-os-mcp',
      translationKey: 'NAVI_OS_MCP',
      icon: 'i-lucide-bot',
      routeName: 'settings_integrations_navi_os_mcp',
      category: 'automation',
    },
    {
      id: 'google-calendar',
      translationKey: 'GOOGLE_CALENDAR',
      icon: 'i-lucide-calendar-days',
      routeName: 'settings_integrations_google_calendar',
      category: 'productivity',
    },
    {
      id: 'scheduling',
      translationKey: 'SCHEDULING',
      icon: 'i-lucide-calendar-check',
      routeName: 'settings_integrations_scheduling',
      category: 'productivity',
    },
    {
      id: 'google-ads',
      translationKey: 'GOOGLE_ADS',
      icon: 'i-lucide-megaphone',
      routeName: 'settings_integrations_google_ads',
      category: 'marketing',
    },
    {
      id: 'meta-leadgen',
      translationKey: 'META_LEADGEN',
      icon: 'i-lucide-facebook',
      routeName: 'settings_integrations_meta_leadgen',
      category: 'marketing',
    },
    {
      id: 'social-comment-automations',
      translationKey: 'SOCIAL_COMMENT_AUTOMATIONS',
      icon: 'i-lucide-message-square-reply',
      routeName: 'settings_integrations_social_comment_automations',
      category: 'marketing',
    },
    {
      id: 'asaas',
      translationKey: 'ASAAS',
      icon: 'i-lucide-wallet-cards',
      routeName: 'settings_integrations_asaas',
      category: 'finance',
    },
  ].map(item => ({
    ...item,
    // Catalog entries are mapped to their existing translation namespace.
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    name: t(`INTEGRATION_SETTINGS.${item.translationKey}.TITLE`),
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    description: t(`INTEGRATION_SETTINGS.${item.translationKey}.DESCRIPTION`),
  }))
);

const legacyIntegrationIds = ['openai', 'dialogflow', 'dyte'];
const integrationCategories = {
  slack: 'communication',
  linear: 'productivity',
  notion: 'productivity',
  shopify: 'commerce',
  woocommerce: 'commerce',
  leadsquared: 'sales',
  webhook: 'automation',
  dashboard_apps: 'automation',
  google_translate: 'communication',
};
const integrationIcons = {
  webhook: 'i-lucide-webhook',
  dashboard_apps: 'i-lucide-layout-dashboard',
  google_translate: 'i-lucide-languages',
  woocommerce: 'i-lucide-shopping-bag',
  leadsquared: 'i-lucide-users-round',
  slack: 'i-lucide-message-square-more',
  linear: 'i-lucide-list-checks',
  notion: 'i-lucide-notebook-tabs',
  shopify: 'i-lucide-shopping-cart',
};

const externalIntegrations = computed(() =>
  getters['integrations/getAppIntegrations'].value
    .filter(item => !legacyIntegrationIds.includes(item.id) || item.enabled)
    .map(item => ({
      ...item,
      icon: integrationIcons[item.id] || 'i-lucide-blocks',
      category: integrationCategories[item.id] || 'automation',
    }))
);
const catalog = computed(() => [
  ...nativeIntegrations.value,
  ...externalIntegrations.value,
]);
const categories = computed(() => [
  'all',
  ...new Set(catalog.value.map(item => item.category)),
]);
const filteredCatalog = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  return catalog.value.filter(item => {
    const matchesCategory =
      selectedCategory.value === 'all' ||
      item.category === selectedCategory.value;
    const content = `${item.name} ${item.description}`.toLocaleLowerCase();
    return matchesCategory && (!query || content.includes(query));
  });
});

onMounted(() => store.dispatch('integrations/get'));
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        icon-name="apps"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5 pb-8">
        <div
          class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-2 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <label
            class="flex h-10 w-full items-center rounded-lg border border-n-weak bg-n-alpha-1 px-3 transition focus-within:border-n-brand sm:max-w-sm"
          >
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('INTEGRATION_SETTINGS.CATALOG.SEARCH')"
              class="h-full min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 !shadow-none !outline-none focus:!border-0 focus:!ring-0"
            />
          </label>
          <p class="m-0 text-sm text-n-slate-10">
            {{
              $t('INTEGRATION_SETTINGS.CATALOG.COUNT', {
                count: filteredCatalog.length,
              })
            }}
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            v-for="category in categories"
            :key="category"
            type="button"
            class="rounded-full border px-3 py-1.5 text-sm font-medium transition"
            :class="
              selectedCategory === category
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
            "
            @click="selectedCategory = category"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
            {{ $t(`INTEGRATION_SETTINGS.CATALOG.CATEGORIES.${category}`) }}
          </button>
        </div>

        <div
          v-if="filteredCatalog.length"
          class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          <IntegrationItem
            v-for="item in filteredCatalog"
            :id="item.id"
            :key="item.id"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
            :icon="item.icon"
            :route-name="item.routeName"
          />
        </div>
        <div
          v-else
          class="flex min-h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-n-weak text-center"
        >
          <i class="i-lucide-search-x size-7 text-n-slate-9" />
          <p class="m-0 text-sm font-medium text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.CATALOG.EMPTY') }}
          </p>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
